import {createAction, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {getActionName} from "../../utils/getActionName";
import {QuickAddItemDto} from "../../common";

const sliceName = 'quickAdd';

export interface QuickAddStore {
    items: QuickAddItemDto[];
    inputText: string;
    isProcessing: boolean;
    expandedIds: string[];
}

export const quickAddInitialState: QuickAddStore = {
    items: [],
    inputText: '',
    isProcessing: false,
    expandedIds: [],
};

function isBehindLocal(local: QuickAddItemDto, incoming: QuickAddItemDto): boolean {
    return incoming.status === 'pending' && local.status !== 'pending';
}

const quickAddSlice = createSlice({
    name: sliceName,
    initialState: quickAddInitialState,
    reducers: {
        setItems(state, action: PayloadAction<QuickAddItemDto[]>) {
            state.items = action.payload;
        },

        mergeItems(state, action: PayloadAction<QuickAddItemDto[]>) {
            for (const incoming of action.payload) {
                const index = state.items.findIndex(i => i.id === incoming.id);
                if (index === -1) {
                    state.items.unshift(incoming);
                } else if (!isBehindLocal(state.items[index], incoming)) {
                    state.items[index] = incoming;
                }
            }
        },

        upsertItem(state, action: PayloadAction<QuickAddItemDto>) {
            const item = action.payload;
            const index = state.items.findIndex(i => i.id === item.id);
            if (index === -1) {
                state.items.unshift(item);
            } else {
                state.items[index] = item;
            }
        },

        removeItemFromStore(state, action: PayloadAction<string>) {
            state.items = state.items.filter(i => i.id !== action.payload);
            state.expandedIds = state.expandedIds.filter(id => id !== action.payload);
        },

        removeFinishedFromStore(state) {
            const finishedIds = new Set(
                state.items.filter(i => i.status === 'done' || i.status === 'error').map(i => i.id)
            );
            state.items = state.items.filter(i => !finishedIds.has(i.id));
            state.expandedIds = state.expandedIds.filter(id => !finishedIds.has(id));
        },

        setInputText(state, action: PayloadAction<string>) {
            state.inputText = action.payload;
        },

        setProcessing(state, action: PayloadAction<boolean>) {
            state.isProcessing = action.payload;
        },

        clearGeneratedCard(state, action: PayloadAction<{itemId: string; direction: 'normal' | 'inverted'}>) {
            const item = state.items.find(i => i.id === action.payload.itemId);
            if (item === undefined) return;
            if (action.payload.direction === 'normal') {
                item.normalCard = undefined;
            } else {
                item.invertedCard = undefined;
            }
        },

        toggleExpand(state, action: PayloadAction<string>) {
            const id = action.payload;
            if (state.expandedIds.includes(id)) {
                state.expandedIds = state.expandedIds.filter(x => x !== id);
            } else {
                state.expandedIds.push(id);
            }
        },
    }
});

export const quickAddReducer = quickAddSlice.reducer;

export const quickAddActions = {
    ...quickAddSlice.actions,
    submit: createAction(getActionName(sliceName, 'submit')),
    loadQueue: createAction(getActionName(sliceName, 'loadQueue')),
    startProcessing: createAction(getActionName(sliceName, 'startProcessing')),
    deleteItem: createAction<string>(getActionName(sliceName, 'deleteItem')),
    deleteGeneratedCard: createAction<{itemId: string; direction: 'normal' | 'inverted'; cardId: string}>(getActionName(sliceName, 'deleteGeneratedCard')),
    retryItem: createAction<string>(getActionName(sliceName, 'retryItem')),
    clearFinished: createAction(getActionName(sliceName, 'clearFinished')),
};
